/**
 * offer-i18n.ts — Lot 8 i18n (28/05/2026)
 *
 * Helpers uniques pour résoudre le nom et la description d'une offre selon la
 * langue active. Évite de répéter la cascade nameFr/En/legacy dans chaque
 * composant. Calqué sur simulator-description-i18n.ts (Lot 7).
 *
 * Cascade :
 *   - language === "fr" → champFr || champEn || champ legacy || ""
 *   - autre (en, ...)    → champEn || champFr || champ legacy || ""
 *
 * Les champs legacy `name`/`description` sont conservés en dernier recours pour
 * les offres pas encore migrées / pas encore retraduites par le cron.
 */

type OfferI18nFields = {
  displayName?: string | null;
  name?: string | null;
  description?: string | null;
  nameFr?: string | null;
  nameEn?: string | null;
  descriptionFr?: string | null;
  descriptionEn?: string | null;
  valueProposition?: string | null;
  valuePropositionFr?: string | null;
  valuePropositionEn?: string | null;
};

export function pickOfferName(
  offer: OfferI18nFields | null | undefined,
  language: string | undefined,
): string {
  if (!offer) return "";
  const isFr = (language || "fr").toLowerCase().startsWith("fr");
  if (isFr) {
    return offer.nameFr || offer.nameEn || offer.name || "";
  }
  return offer.nameEn || offer.nameFr || offer.name || "";
}

/** Label d'affichage UI : titre optionnel s'il est présent, sinon nom marketing.
 *  Cf. Mariam 10/07/26 — à utiliser dans les listes/cartes/wizards UI,
 *  JAMAIS dans le payload N8N (utiliser pickOfferName pour ça). */
export function pickOfferLabel(
  offer: OfferI18nFields | null | undefined,
  language: string | undefined,
): string {
  if (!offer) return "";
  if (typeof offer.displayName === "string" && offer.displayName.trim().length > 0) {
    return offer.displayName.trim();
  }
  return pickOfferName(offer, language);
}

export function pickOfferDescription(
  offer: OfferI18nFields | null | undefined,
  language: string | undefined,
): string {
  if (!offer) return "";
  const isFr = (language || "fr").toLowerCase().startsWith("fr");
  if (isFr) {
    return offer.descriptionFr || offer.descriptionEn || offer.description || "";
  }
  return offer.descriptionEn || offer.descriptionFr || offer.description || "";
}

/**
 * Proposition de valeur de l'offre, dans la langue active.
 * Même cascade que le nom et la description — ne JAMAIS lire le champ brut.
 */
export function pickOfferValueProposition(
  offer: OfferI18nFields | null | undefined,
  language: string | undefined,
): string {
  if (!offer) return "";
  const isFr = (language || "fr").toLowerCase().startsWith("fr");
  if (isFr) {
    return offer.valuePropositionFr || offer.valuePropositionEn || offer.valueProposition || "";
  }
  return offer.valuePropositionEn || offer.valuePropositionFr || offer.valueProposition || "";
}
